import { faPlayCircle } from '@fortawesome/pro-regular-svg-icons/faPlayCircle';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';

import React, { ReactNode, useEffect, useState } from 'react';

import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text0 } from '@osmosis/tailwind-ui/styles';
import { getColor } from '@osmosis/shared/styles';
import styles from './VideoOverlay.module.css';

import { useIntl, FormattedMessage } from 'react-intl';

interface Props {
  components?: ReactNode[];
}

/**
 * Return VideoOverlay component.
 */
export default function VideoOverlay(props: Props): JSX.Element {
  const { components } = props;
  const { playing, startedState, handlePlayPause, isReady } = useVideoPlayer();
  const [isStarted] = startedState;

  const [playOnLoad, setPlayOnLoad] = useState(false);

  const [isAnimatingPlay, setIsAnimatingPlay] = useState(false);

  useEffect(() => {
    setIsAnimatingPlay(true);
    setTimeout(() => {
      setIsAnimatingPlay(false);
    }, 250);
  }, [playing]);

  const intl = useIntl();

  const handlePlay = () => {
    if (isReady) {
      handlePlayPause();
    } else {
      // Add flag to play video when loaded
      setPlayOnLoad(true);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    action: () => void,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  // Callback to check if we user tried to play the video before video was loaded
  useEffect(() => {
    if (isReady && playOnLoad) {
      handlePlayPause();
      setPlayOnLoad(false);
    }
  }, [isReady]);

  return (
    <>
      {/* Display components passed as props first (ex. prime lockout) */}
      {components}

      {/* Display the overlay if the video has not started playing */}
      {!isStarted && (
        <Box
          className={
            'absolute top-0 left-0 right-0 bottom-0 transition-all duration-300 ease-in-out cursor-pointer h-[calc(100%-70px)] bg-[rgba(0,78,150,0.8)] hover:bg-[rgba(0,78,150,0.9)] group'
          }
          onClick={handlePlay}
          onKeyDown={(event) => handleKeyDown(event, handlePlay)}
          role="button"
          tabIndex={0}
          aria-label={intl.formatMessage({
            id: 'video_overlay.play',
            defaultMessage: 'Play video',
          })}
          data-testid="overlayNotStarted"
        >
          <Box
            className={cn(
              'text-7xl',
              text0,
              'cursor-pointer absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
            )}
          >
            <Flex
              className={cn(
                'overlay-play-icon rounded-full transition-all duration-200 ease-in-out fill-current',
                text0,
                'group-hover:bg-gray-900 group-hover:scale-125',
              )}
              data-testid="notStartedIcon"
              aria-hidden="true"
            >
              <Icon icon={faPlayCircle} />
            </Flex>
          </Box>
        </Box>
      )}

      {/* Display the overlay if the avideo has started playing */}
      {isStarted && (
        <Box
          onClick={handlePlayPause}
          onKeyDown={(event) => handleKeyDown(event, handlePlayPause)}
          role="button"
          tabIndex={0}
          aria-label={intl.formatMessage({
            id: playing ? 'video_overlay.pause' : 'video_overlay.play',
            defaultMessage: playing ? 'Pause video' : 'Play video',
          })}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 'calc(100% - 70px)',
          }}
          className="w-full"
          data-testid="overlayPlayPause"
        >
          <>
            <Box
              data-testid="playPauseIndicator"
              className={cn(
                isAnimatingPlay
                  ? styles['animate-play-pulse']
                  : 'scale-1 opacity-0',
                'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-900 h-[60px] w-[60px] flex items-center justify-center z-[99999] fill-white',
                text0,
              )}
            >
              <Icon
                icon={playing ? faPlay : faPause}
                style={{ fontSize: '30px' }}
              />
            </Box>
          </>
        </Box>
      )}

      {/* Tooltips for video top controls (add to playist/watch later) */}
      <Tooltip
        id="add-to-playlist"
        style={{
          backgroundColor: getColor('light', 'gray80'),
          color: getColor('light', 'textInvert'),
        }}
      >
        <P color="inherit">
          <FormattedMessage
            defaultMessage="Add to playlist"
            id="common.addToPlaylist"
          />
        </P>
      </Tooltip>
    </>
  );
}
